import {
    Component,
    Input,
    ViewChild,
    TemplateRef,
    ElementRef,
    AfterViewInit,
    AfterViewChecked,
    EventEmitter,
    Output, OnInit,
    OnDestroy,
    ChangeDetectorRef,
    Renderer2,
    NgZone,
    QueryList,
    ViewChildren
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { DomHandler } from 'primeng/dom';

import { CustomDropdownOption, DropdownOptionModel } from '../../models/dropdown-option.model';
import { TooltipDirective } from '../../../../Common/Basic/directives/tooltip.directive';
import { DropdownTypesEnum } from '../../enums/dropdown-types.enum';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ActionKeys } from '../../enums/keys.enum';


/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the dropdown has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaLabel*** - use this to provide an accessible label for the input. Don't use this if ariaLabelledBy is used
 * + ***ariaLabelledBy*** - use this to associate an external label with the input. Use id of the label as this value. This overwrites ariaLabel
 * + ***ariaRequired*** - use this to indicate that the value is required. Prefer required property, and only use this when the asterisk is not necessary
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***isInTable*** - use this to handle focusability if the dropdown is in a table
 */
@Component({
    selector: 'agpr-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    animations: [
        trigger('panelState', [
            state('hidden', style({
                opacity: 0
            })),
            state('visible', style({
                opacity: 1
            })),
            transition('visible => hidden', animate('400ms ease-in')),
            transition('hidden => visible', animate('400ms ease-out'))
        ])
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DropdownComponent,
            multi: true,
        }
    ],
    standalone: false
})
export class DropdownComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy, ControlValueAccessor {

    readonly agprComponentStyle = AgprComponentStyleEnum;

    @Input() id: string;
    @Input() placeholder: string;
    @Input() disabled: boolean = false;
    @Input() required: boolean = false;
    @Input() panelStyleClass: string = '';
    @Input() labelClass: string = '';
    @Input() labelStyleClass: string = '';
    @Input() optionLabel: string;
    @Input() label: string;
    @Input() infoCircleTooltip: string;
    @Input() tooltipPath: string;
    @Input() itemTooltipPosition: string;
    @Input() inputTooltip: string;
    @Input() inputTooltipPosition: string = 'top';
    @Input() inputTooltipStyleClass: string;
    @Input() style: DropdownTypesEnum = DropdownTypesEnum.normal;
    @Input() itemTemplate: TemplateRef<any>;
    @Input() agprStyle: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() ariaLabel: string;
    @Input() ariaLabelledBy: string;
    @Input() ariaDescribedBy: string = '';
    @Input() ariaRequired: boolean = false;
    @Input() allowNull: boolean = false;
    @Input() selectOnFocus: boolean;
    @Input() keepSelectedValue: boolean = true;
    @Input() isInTable: boolean = false;
    // custom options will their specific action when selected
    @Input() customOptions: CustomDropdownOption[];
    @Input() showHiddenOption: boolean = false;
    @Input() showInput: boolean = true;
    @Input() replaceInputIcon: string;
    @Input() optionStyleClass: string;
    @Input() callToAction: CustomDropdownOption;
    @Input() showTooltip?: boolean;
    @Input() dropdownContainerStyleClass: string = '';

    // property name that is used to find the selected option
    @Input() key: string;

    /* Used to bind by multi-field-chips width of label to length of label for responsive widths */
    @Input() scaleDropdownLabel: boolean = false;

    // If true, the actual value used for the binding will be the "value" attribute of the DropdownOptionModel
    // this allows to bind directly to a model without packing and unpacking as DropdownOptionModel
    @Input() bindToValue: boolean = false;
    @Input() alignPanelRight: boolean = false;

    @Output() change: EventEmitter<any> = new EventEmitter();
    @Output() focus: EventEmitter<any> = new EventEmitter();
    @Output() blur: EventEmitter<any> = new EventEmitter();
    @Output() click: EventEmitter<any> = new EventEmitter();

    @ViewChild('dropdown') dropdown: ElementRef;
    @ViewChild('container') containerViewChild: ElementRef;
    @ViewChild('panel') panelViewChild: ElementRef;
    @ViewChild('itemswrapper') itemsWrapperViewChild: ElementRef;
    @ViewChild('focusInput') focusViewChild: ElementRef;
    @ViewChildren('directives', {read: TooltipDirective}) directives: QueryList<TooltipDirective>;

    public selfClick: boolean;
    public itemClick: boolean;
    public panelVisible: boolean = false;
    public panel: HTMLDivElement;
    public shown: boolean;
    public container: HTMLDivElement;
    public optionsToDisplay: any[];
    public documentClickListener: any;
    public optionsChanged: boolean;
    public itemsWrapper: HTMLDivElement;
    public initialized: boolean;
    public focusedOptionUpdated: boolean;
    public value: DropdownOptionModel;
    public scrollableParents: any;
    public focusedItemIndex: number = -1;
    public selectedItemIndex: number = -1;
    public showHidden: boolean = false;
    public hiddenCount: number = 0;
    isTruncated: boolean = false;

    // Shows the full label in a tooltip when truncated (default style, showTooltip only)
    isInputTruncated: boolean = false;

    dropdownTypesEnum = DropdownTypesEnum;

    _options: DropdownOptionModel[] | any[];

    /**
     * A bound value written before an option carrying it exists - the model set before the options
     * load. Held so the options setter can still select it, rather than the value being dropped.
     */
    private unresolvedValue: any;

    constructor(
        public el: ElementRef,
        public renderer: Renderer2,
        public cd: ChangeDetectorRef,
        public zone: NgZone
    ) {}

    private readonly eventListener = () =>  {
        this.hide();
    };

    ngOnInit(): void {
        this.optionsToDisplay = this.options;
        if (this.showHiddenOption) {
            this.optionsToDisplay = this.options?.filter(option => !option.value.hidden);
            this.hiddenCount = this.options?.filter(option => option.value.hidden).length;
        }
        this.updateFocusedOption(null);
    }

    // Get options to display
    @Input() get options(): DropdownOptionModel[] | any[] {
        return this._options;
    }
    set options(options: DropdownOptionModel[] | any[]) {
        this._options = options;
        this.optionsToDisplay = this._options;

        // What is displayed is the option object writeValue resolved, so a list rebuilt after that
        // - options that arrive with their labels filled in, say - has to be matched against the
        // value again, or the dropdown goes on showing the option it was handed first
        if (this.bindToValue && (this.value || this.unresolvedValue != null)) {
            const wanted = this.value ? this.value.value : this.unresolvedValue;
            const selected = this._options?.find(option => option.value === wanted);

            if (selected) {
                this.value = selected;
                this.unresolvedValue = undefined;
                this.selectedItemIndex = this.findOptionIndex(this.value, this.optionsToDisplay, this.key);
            }
        }

        this.updateFocusedOption(this.value);
        this.optionsChanged = true;
    }

    ngAfterViewInit() {
        // @TODO: These exist because domHandler needs them
        this.container = <HTMLDivElement>this.containerViewChild.nativeElement;
        this.panel = <HTMLDivElement>this.panelViewChild.nativeElement;
        this.itemsWrapper = <HTMLDivElement>this.itemsWrapperViewChild.nativeElement;
        if (this.isInTable) {
            this.focusViewChild?.nativeElement?.setAttribute('tabindex', '-1');
        }

        this.initialized = true;

        document.body.appendChild(this.panel);
        this.updateInputTruncation();
    }


    checkTruncatedText(textElement: HTMLElement): void {
        this.isTruncated = textElement.scrollWidth > textElement.clientWidth;
    }

    /**
     * Required by ControlValueAccessor
	 * to identify when the model has changed
     */
    onModelChange: Function = () => { };

    /**
     * Required by ControlValueAccessor
     * to identify when the input has been touched
     */
    onModelTouched: Function = () => { };

    ngAfterViewChecked() {
        if (this.shown) {
            this.onShow();
            this.shown = false;
        }

        if (this.optionsChanged && this.panelVisible) {
            this.optionsChanged = false;

            this.zone.runOutsideAngular(() => {
                setTimeout(() => {
                    this.alignPanel();
                }, 1);
            });
        }

        if (this.focusedOptionUpdated && this.panel) {
            const focusedItem = DomHandler.findSingle(this.panel, 'li.agpr-dropdown-panel-focusedItem');
            if (focusedItem) {
                DomHandler.scrollInView(this.panel, DomHandler.findSingle(this.panel, 'li.agpr-dropdown-panel-focusedItem'));
                setTimeout(() => {
                    this.directives.forEach(x => x?.deactivate());
                    this.directives.toArray()[this.focusedItemIndex]?.activate();
                    this.focusedOptionUpdated = false;
                }, 0);
            }

        }
    }

    /**
     * Required by ControlValueAccessor
     * to write the value to the view if the value changes occur on the model programmatically
	 */
    writeValue(value: any): void {
        if ((value === undefined || value === null) && !this.allowNull) {
            return;
        }
        if (this.bindToValue) {
            this.value = this.options?.find(x => x.value === value);
            this.unresolvedValue = this.value ? undefined : value;
        } else {
            this.value = value;
        }
        this.updateFocusedOption(value);
        this.selectedItemIndex = this.findOptionIndex(this.value, this.optionsToDisplay, this.key);
        this.cd.markForCheck();
        this.updateInputTruncation();
    }

    updateFocusedOption(val: any): void {
        this.focusedOptionUpdated = true;
    }

    /**
     * Required by ControlValueAccessor
     * register a callback function to know when the control's value changes in the UI
     */
    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    /**
     * Required by ControlValueAccessor
     * register a callback function to know when the model has been blurred or "touched"
     */
    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onKeydown(event: KeyboardEvent): void {
        if (!this.optionsToDisplay || this.optionsToDisplay.length === null) {
            return;
        }

        switch (event.key) {
            case ActionKeys.DOWN:
                if (this.isInTable && !this.panelVisible) {
                    break;
                }
                if (!this.panelVisible) {
                    this.show();
                    break;
                }
                if (this.focusedItemIndex === -1) {
                    this.focusItem(this.value ? this.findOptionIndex(this.value, this.optionsToDisplay, this.key) : 0);
                } else {
                    this.focusItem(Math.min(this.optionsToDisplay.length - 1, this.focusedItemIndex + 1));
                }
                break;
            case ActionKeys.UP:
                if (this.focusedItemIndex > 0) {
                    this.focusItem(this.focusedItemIndex - 1);
                } else if (!this.isInTable) {
                    this.hide();
                }
                break;
            case ActionKeys.HOME:
                this.focusItem(0);
                break;
            case ActionKeys.END:
                this.focusItem(this.optionsToDisplay.length - 1);
                break;
            case ActionKeys.PAGEUP:
                this.focusItem(Math.max(0, this.focusedItemIndex - 10));
                break;
            case ActionKeys.PAGEDOWN:
                this.focusItem(Math.min(this.optionsToDisplay.length - 1, this.focusedItemIndex + 10));
                break;
            case ActionKeys.ENTER:
            case ActionKeys.SPACE:
                if (!this.panelVisible) {
                    this.show();
                } else {
                    if (this.focusedItemIndex > -1) {
                        this.selectItem(event, this.focusedItemIndex);
                    }
                    this.hide();
                }
                break;
            case ActionKeys.ESCAPE:
                if (!this.panelVisible) {
                    break;
                }
                this.hide();
                break;
            case ActionKeys.TAB:
                if (this.focusedItemIndex > -1) {
                    this.selectItem(event, this.focusedItemIndex);
                }
                if (this.panelVisible) {
                    this.hide();
                }
                return;
        }
        event.preventDefault();
        event.stopPropagation();
    }

    // The main operations are handled by keydown. However, table operates on keyup event, so don't emit keyup event, especially in table
    onKeyup(event: KeyboardEvent): void {
        if (this.panelVisible) {
            event.stopPropagation();
        }
        if (event.key === ActionKeys.ESCAPE) {
            event.stopImmediatePropagation();
        }
    }

    onInputFocus(event) {
        this.focus.emit(event);
    }

    onInputBlur(event) {
        this.onModelTouched();
        this.blur.emit(event);
    }

    show() {
        this.panel.style.minWidth = DomHandler.getWidth(this.container) + 'px';
        this.panel.style.zIndex = String(++DomHandler.zindex);
        this.panelVisible = true;
        this.shown = true;
    }

    hide() {
        this.focusViewChild?.nativeElement?.setAttribute('aria-activedescendant', '');
        this.panelVisible = false;
        this.focusedItemIndex = -1;
    }

    onShow() {
        this.bindDocumentClickListener();
        this.addEventListeners();

        if (this.options && this.options.length) {
            this.alignPanel();

            const selectedListItem = DomHandler.findSingle(this.itemsWrapper, 'li.agpr-dropdown-panel-selectedItem');
            if (selectedListItem) {
                requestAnimationFrame(() => {
                    this.focusItem(this.focusedItemIndex);
                    DomHandler.scrollInView(this.itemsWrapper, selectedListItem);
                });
            }
        }
    }

    onMouseclick(event) {
        if (this.disabled) {
            return;
        }

        this.selfClick = true;

        if (!this.itemClick) {
            if (this.panelVisible) {
                this.hide();
            } else {
                this.show();
            }
        }

        this.focusViewChild?.nativeElement?.focus();
    }

    onItemClick(event, index: number): void {
        this.itemClick = true;
        this.selectItem(event, index);
        this.hide();
    }

    focusItem(index: number): void {
        if (index === this.focusedItemIndex) {
            return;
        }
        this.focusViewChild?.nativeElement?.setAttribute('aria-activedescendant', this.id + '_dropdownOption_' + index);
        this.focusedItemIndex = index;
        this.focusedOptionUpdated = true;
    }

    selectItem(event, index: number) {
        if (this.optionsToDisplay[index]?.disabled) {
            return;
        }
        this.value = this.optionsToDisplay[index];
        this.selectedItemIndex = index;
        if (!this.bindToValue) {
            this.onModelChange(this.value);
            this.change.emit({
                originalEvent: event,
                value: this.value
            });
        } else {
            this.onModelChange(this.value.value);
            this.change.emit({
                originalEvent: event,
                value: this.value.value
            });
        }
        if (!this.keepSelectedValue) {
            this.value = null;
            this.selectedItemIndex = -1;
        }
        this.updateInputTruncation();
    }

    alignPanel() {
        DomHandler.absolutePosition(this.panel, this.container);
        if (this.alignPanelRight) {
            this.panel.style.left = (this.panel.offsetLeft - (this.panel.offsetWidth - this.container.offsetWidth)) + 'px';
        }
    }

    findOptionIndex(val: any, opts: any[], key?: string): number {
        if (opts) {
            if (key) {
                return opts.map(x => x[key]).indexOf(val[key]);
            } else {
                return opts.indexOf(val);
            }
        }
        return -1;
    }

    findOptionGroupIndex(val: any, opts: any[]): any {
        let groupIndex, itemIndex;

        if (opts) {
            for (let i = 0; i < opts.length; i++) {
                groupIndex = i;
                itemIndex = this.findOptionIndex(val, opts[i].items, this.key);

                if (itemIndex !== -1) {
                    break;
                }
            }
        }

        if (itemIndex !== -1) {
            return { groupIndex: groupIndex, itemIndex: itemIndex };
        } else {
            return -1;
        }
    }

    addEventListeners() {
        this.scrollableParents = DomHandler.getScrollableParents(this.el.nativeElement);

        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.addEventListener('scroll', this.eventListener);
            });
        }
        window.addEventListener('resize', this.eventListener);
    }

    removeEventListeners() {
        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.removeEventListener('scroll', this.eventListener);
            });
        }

        window.removeEventListener('resize', this.eventListener);
    }

    /**
     * Closes panel on clicks outside the dropdown.
     */
    bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.documentClickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
                // After the Angular 21 upgrade, `eventCoalescing: true` was enabled for zone change
                // detection in bootstrap.module.ts. Instead of triggering change detection
                // synchronously after each DOM event, Angular now batches/coalesces event-driven
                // change detection and schedules it via a callback — effectively deferred relative
                // to the triggering event, instead of running immediately. So when the popover is
                // shown and this listener gets bound (from ngAfterViewChecked(), see onShow()), it no
                // longer fires in time to catch that same click, and this.selfClick is still `true`
                // (it was set in onMouseclick()). That stale flag would make the next real outside
                // click get misread as a self-click, so instead of relying on selfClick/itemClick, we
                // check DOM containment of the click's actual target.
                const target = event.target as Node;
                const isInsideContainer = !!this.container?.contains(target);
                const isInsidePanel = !!this.panel?.contains(target);
                if (!isInsideContainer && !isInsidePanel) {
                    this.panelVisible = false;
                    this.unbindDocumentClickListener();
                }

                this.selfClick = false;
                this.itemClick = false;
                this.cd.markForCheck();
            });
        }
    }

    unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    toggleHiddenItems(event: MouseEvent) {
        event.stopImmediatePropagation();
        this.showHidden = !this.showHidden;
        if (this.showHidden) {
            this.optionsToDisplay = this.options;
        } else {
            this.optionsToDisplay = this.options.filter(option => !option.value.hidden);
        }
    }

    updateInputTruncation(): void {
        if (!this.showTooltip) {
            return;
        }
        // HACK: Wait two animation frames for the input layout to settle before checking truncation
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const input = this.focusViewChild?.nativeElement;
                this.isInputTruncated = input instanceof HTMLInputElement && input.scrollWidth > input.clientWidth;
                this.cd.markForCheck();
            });
        });
    }

    ngOnDestroy() {
        this.initialized = false;

        this.unbindDocumentClickListener();
        this.removeEventListeners();

        if (this.panel) {
            this.panel.parentNode.removeChild(this.panel);
        }
    }
}
